import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { CalendarClock, Trash2, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { prettyDate } from "@/lib/geo";

export const today = () => new Date().toISOString().slice(0, 10);

export const dueState = (r) => {
  if (!r.follow_up || r.status === "booked") return null;
  const t = today();
  return r.follow_up < t ? "overdue" : r.follow_up === t ? "today" : null;
};

const useUpdate = () => {
  const qc = useQueryClient();
  return (next) => qc.setQueryData(["trip-requests"], (list) => list.map((x) => (x.request_id === next.request_id ? { ...x, ...next } : x)));
};

export const DueTag = ({ r }) => {
  const s = dueState(r);
  if (!s) return null;
  return (
    <span className={`ml-2 align-middle px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-wide ${s === "overdue" ? "bg-red-500/20 text-red-300" : "bg-amber-400/20 text-amber-200"}`} data-testid={`trip-due-${r.request_id}`}>
      {s === "overdue" ? "Overdue" : "Follow up today"}
    </span>
  );
};

export const FollowUp = ({ r }) => {
  const update = useUpdate();
  const save = async (follow_up) => {
    update({ request_id: r.request_id, follow_up });
    await api.setFollowUp(r.request_id, follow_up).catch(() => toast.error("Could not save follow-up date"));
  };
  return (
    <label className="flex items-center gap-1.5 text-[11px] text-slate-400 rounded-lg border border-white/10 px-2 py-1 w-fit">
      <CalendarClock size={12} /> Follow up
      <input type="date" value={r.follow_up || ""} onChange={(e) => save(e.target.value)}
        className="bg-transparent outline-none text-slate-200 font-mono [color-scheme:dark]" data-testid={`trip-follow-up-${r.request_id}`} />
    </label>
  );
};

export const NotesLog = ({ r }) => {
  const update = useUpdate();
  const [text, setText] = useState("");
  const notes = r.notes || [];
  const add = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      update(await api.addTripNote(r.request_id, text.trim()));
      setText("");
    } catch {
      toast.error("Could not save note");
    }
  };
  const remove = async (id) => update(await api.deleteTripNote(r.request_id, id));
  return (
    <div className="mt-3 rounded-lg border border-white/10 bg-white/[0.02] p-3" data-testid={`trip-notes-${r.request_id}`}>
      <p className="eyebrow !text-slate-500 mb-2">Private notes · {notes.length}</p>
      {notes.length > 0 && (
        <ol className="space-y-2 mb-3">
          {notes.map((n) => (
            <li key={n.id} className="group flex gap-3 text-sm" data-testid={`trip-note-${n.id}`}>
              <span className="font-mono text-[10px] text-slate-500 pt-1 w-16 shrink-0">{prettyDate(n.at.slice(0, 10), "d MMM yy")}</span>
              <span className="flex-1 whitespace-pre-line text-slate-200">{n.text}</span>
              <button onClick={() => remove(n.id)} className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-red-400 transition-opacity" aria-label="Delete note" data-testid={`trip-note-delete-${n.id}`}><Trash2 size={12} /></button>
            </li>
          ))}
        </ol>
      )}
      <form onSubmit={add} className="flex gap-2">
        <input value={text} onChange={(e) => setText(e.target.value)} maxLength={4000} placeholder="Add a note: called, sent options, waiting on dates…"
          className="flex-1 bg-transparent border-b border-white/10 focus:border-emerald-400/60 outline-none text-sm py-1 placeholder:text-slate-600" data-testid={`trip-note-input-${r.request_id}`} />
        <button type="submit" disabled={!text.trim()} className="text-emerald-300 hover:text-emerald-200 disabled:opacity-30" aria-label="Save note" data-testid={`trip-note-add-${r.request_id}`}><Send size={14} /></button>
      </form>
    </div>
  );
};
